import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  QueryList,
  ViewChildren,
  OnChanges,
  inject,
  PLATFORM_ID,
  afterNextRender,
  ViewContainerRef,
  ComponentRef,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { MediaVideoModel } from '@modules/media/models/media-video.model';
import { WindowService } from '@modules/shared/services/window.service';
import {
  ActionIcons,
  ButtonItem,
  ActionContainerComponent,
} from '@modules/ui/action-container/action-container.component';

import { MediaComponent } from '../../media/media.component';
import { FileSizePipe } from '../../pipes/file-size.pipe';
import { VideoWithIndex } from '../interfaces/video-with-index.interface';

import type { MasonryGalleryContentComponent } from './masonry-gallery-content/masonry-gallery-content.component';

@Component({
  selector: 'mb-masonry-gallery',
  templateUrl: './masonry-gallery.component.html',
  styleUrls: ['./masonry-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MediaComponent, ActionContainerComponent, FileSizePipe, TranslatePipe],
})
export class MasonryGalleryComponent implements OnInit, OnChanges {
  private windowService = inject(WindowService);
  private cdr = inject(ChangeDetectorRef);
  private platformId = inject(PLATFORM_ID);

  public displayedVideos: MediaVideoModel[] = [];
  public allVideosVisible = false;
  public showButtons = false;
  public page = 1;
  public masonryLoaded = false;

  private readonly ITEMS_PER_PAGE = 6;
  private masonryContentRef?: ComponentRef<MasonryGalleryContentComponent>;

  @ViewChild('scrollAnchor') scrollAnchor!: ElementRef<any>;
  @ViewChild('masonryContainer', { read: ViewContainerRef }) masonryContainer!: ViewContainerRef;
  @ViewChildren('videoGridItem') placeholderGridItems!: QueryList<any>;

  @Input() items: MediaVideoModel[] = [];
  @Input() buttonItems: ButtonItem[] = [];

  @Output() itemClicked = new EventEmitter<VideoWithIndex>();
  @Output() itemActionClicked = new EventEmitter<{ action: ActionIcons; media: MediaVideoModel }>();

  constructor() {
    afterNextRender(() => {
      this.loadMasonryContent();
    });
  }

  ngOnInit() {
    this.displayedVideos = this.items.slice(0, this.page * this.ITEMS_PER_PAGE);
    if (this.displayedVideos.length >= this.items.length) {
      this.allVideosVisible = true;
    } else {
      this.showButtons = true;
    }
  }

  private async loadMasonryContent(): Promise<void> {
    try {
      if (!this.masonryContainer) return;

      const { MasonryGalleryContentComponent } =
        await import('./masonry-gallery-content/masonry-gallery-content.component');
      this.masonryContainer.clear();
      this.masonryContentRef = this.masonryContainer.createComponent(MasonryGalleryContentComponent);

      this.updateMasonryInputs();

      this.masonryContentRef.instance.itemClicked.subscribe((e) => this.itemClicked.emit(e));
      this.masonryContentRef.instance.itemActionClicked.subscribe((e) => this.itemActionClicked.emit(e));

      this.masonryLoaded = true;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to load Masonry module', error);
    }
  }

  private updateMasonryInputs() {
    if (this.masonryContentRef) {
      this.masonryContentRef.instance.displayedVideos = this.displayedVideos;
      this.masonryContentRef.instance.buttonItems = this.buttonItems;
      this.masonryContentRef.changeDetectorRef.detectChanges();
    }
  }

  ngOnChanges() {
    if (this.items && this.items.length > 0) {
      const currentDisplayedCount = this.displayedVideos.length;

      // Update displayedVideos with new content but maintain the same count
      if (currentDisplayedCount > 0) {
        this.displayedVideos = this.items.slice(0, currentDisplayedCount);
      } else {
        // Initial load case
        this.displayedVideos = this.items.slice(0, this.page * this.ITEMS_PER_PAGE);
      }

      // Update visibility flags based on new items
      this.allVideosVisible = this.displayedVideos.length >= this.items.length;
      this.showButtons = !this.allVideosVisible;

      this.updateMasonryInputs();
      this.cdr.detectChanges();
    }
  }

  public onToggleAllClick(): void {
    if (this.displayedVideos.length < this.items.length) {
      this.incrementScroll();
      this.displayedVideos = this.items;
      this.allVideosVisible = true;
    } else {
      this.page = 1;
      this.displayedVideos = this.items.slice(0, this.page * this.ITEMS_PER_PAGE);
      this.allVideosVisible = false;
      this.resetScrollPosition();
    }
    this.updateMasonryInputs();
  }

  public onPagerButtonClick(): void {
    this.page++;
    this.incrementScroll();
    this.displayedVideos = this.items.slice(0, this.page * this.ITEMS_PER_PAGE);
    this.allVideosVisible = this.displayedVideos.length >= this.items.length;
    this.updateMasonryInputs();
    this.cdr.detectChanges();
  }

  private resetScrollPosition(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const scrollTargetRect = this.scrollAnchor.nativeElement.getBoundingClientRect();
    const calc = scrollTargetRect.top + window.pageYOffset - 200;
    this.windowService.scrollTo(calc, 0);
  }

  private incrementScroll(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    let items: ElementRef[];
    if (this.masonryLoaded && this.masonryContentRef) {
      items = this.masonryContentRef.instance.videoGridItems.toArray();
    } else {
      items = this.placeholderGridItems.toArray();
    }

    if (items.length === 0) return;

    const tops = items.map((item) => item.nativeElement.getBoundingClientRect().top).sort();
    this.windowService.scrollTo(window.pageYOffset - tops[tops.length - 1], 0);
  }
}
